// Broker New Deal Submission — per-section AI prompts panel on the
// template-edit page. Mirrors the Collection CollectionPromptEditor but is
// scoped to a single section: each section gets a Feedback prompt (advisory
// guidance shown to the broker when they click Evaluate & Feedback) and an
// Assessment prompt (the staff distillation). Only rendered for a saved
// section — the parent hides it for unsaved/new sections because a prompt
// keys on the persisted section id.

import { useEffect, useState } from "react";
import { Button, Textarea, Card, CardContent } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import {
  dealSubmissionDocumentPromptsApi,
  dealSubmissionPromptsApi,
  type DealSubmissionPromptDto,
  type DealSubmissionPromptKind,
  type DealSubmissionPromptOutputLength,
} from "@/api/dealSubmissionTemplates";

interface DealSubmissionPromptEditorProps {
  templateId: string;
  /** Section-scoped editor. Mutually exclusive with documentRequestId. */
  sectionId?: string;
  /** AI evaluation — document-scoped editor; routes through
   *  dealSubmissionDocumentPromptsApi instead. */
  documentRequestId?: string;
  initialPrompts: DealSubmissionPromptDto[];
}

const KIND_LABELS: Record<
  DealSubmissionPromptKind,
  { title: string; description: string }
> = {
  Feedback: {
    title: "Feedback prompt",
    description:
      "Runs when the broker clicks Evaluate & Feedback — advisory guidance shown inline before they submit. Friendly + concrete; coaches, never blocks.",
  },
  Assessment: {
    title: "Assessment prompt",
    description:
      "Runs from the explicit Evaluate & Feedback action — produces this section's distilled summary shown to the staff reviewer in the staging queue.",
  },
};

export function DealSubmissionPromptEditor({
  templateId,
  sectionId,
  documentRequestId,
  initialPrompts,
}: DealSubmissionPromptEditorProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-4">
      {(["Feedback", "Assessment"] as const).map((kind) => (
        <PromptCard
          key={kind}
          templateId={templateId}
          sectionId={sectionId}
          documentRequestId={documentRequestId}
          kind={kind}
          initial={initialPrompts.find((p) => p.kind === kind) ?? null}
        />
      ))}
    </div>
  );
}

interface PromptCardProps {
  templateId: string;
  sectionId?: string;
  documentRequestId?: string;
  kind: DealSubmissionPromptKind;
  initial: DealSubmissionPromptDto | null;
}

function PromptCard({
  templateId,
  sectionId,
  documentRequestId,
  kind,
  initial,
}: PromptCardProps) {
  const { toast } = useToast();
  const [savedPrompt, setSavedPrompt] = useState<DealSubmissionPromptDto | null>(initial);
  const [body, setBody] = useState(initial?.body ?? "");
  const [outputLength, setOutputLength] = useState<DealSubmissionPromptOutputLength>(
    initial?.outputLength ?? "Medium",
  );
  const [version, setVersion] = useState(initial?.version ?? 0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSavedPrompt(initial);
    setBody(initial?.body ?? "");
    setOutputLength(initial?.outputLength ?? "Medium");
    setVersion(initial?.version ?? 0);
  }, [initial?.id, initial?.body, initial?.outputLength, initial?.version]);

  const dirty =
    body.trim() !== (savedPrompt?.body ?? "").trim()
    || outputLength !== (savedPrompt?.outputLength ?? "Medium");
  const canSave = !!body.trim() && !saving;
  const meta = KIND_LABELS[kind];

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    try {
      const saved = documentRequestId
        ? await dealSubmissionDocumentPromptsApi.save(
            templateId,
            documentRequestId,
            kind,
            body.trim(),
            outputLength,
          )
        : await dealSubmissionPromptsApi.save(
            templateId,
            sectionId!,
            kind,
            body.trim(),
            outputLength,
          );
      setSavedPrompt(saved);
      setBody(saved.body);
      setOutputLength(saved.outputLength ?? "Medium");
      setVersion(saved.version);
      toast({ tone: "success", message: `${meta.title} v${saved.version} saved.` });
    } catch (err) {
      toast({
        tone: "error",
        message:
          err instanceof Error ? err.message : `Could not save the ${kind} prompt.`,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-space-3 p-space-4">
        <header>
          <h3 className="text-h5 font-display text-onyx m-0">{meta.title}</h3>
          <p className="text-caption text-gray-mid mt-space-1 m-0">
            {meta.description}
          </p>
          {version > 0 && (
            <p className="text-caption text-gray-mid mt-space-1">Saved as v{version}.</p>
          )}
        </header>
        <label className="flex flex-col gap-space-1 text-small text-onyx">
          <span className="text-caption text-gray-mid">Output length</span>
          <select
            className="h-10 rounded-sm border border-border-light bg-white-pure px-space-2 text-small"
            value={outputLength}
            onChange={(e) =>
              setOutputLength(e.target.value as DealSubmissionPromptOutputLength)
            }
            aria-label={`${meta.title} output length`}
          >
            <option value="Short">Short</option>
            <option value="Medium">Medium</option>
            <option value="Long">Long</option>
          </select>
          <span className="text-caption text-gray-mid">
            Guides verbosity and token budget; the prompt body controls what the AI reviews.
          </span>
        </label>
        <Textarea
          rows={10}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={
            kind === "Feedback"
              ? "Write the feedback prompt body Claude should use when the broker clicks Evaluate & Feedback…"
              : "Write the assessment prompt body Claude should use to distill this section…"
          }
          aria-label={`${meta.title} body`}
        />
        <div className="flex items-center justify-end">
          <Button type="button" disabled={!canSave || !dirty} onClick={handleSave}>
            {saving ? "Saving…" : dirty ? "Save prompt" : "Saved"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
